import React, { useState, useEffect } from "react";
import axios from "axios";
import Card from "components/card";
import { readToken } from "utils/session";
import { format } from "date-fns";

const Notifications = () => {
  const [alerts, setAlerts] = useState([]);
  const [fleetDids, setFleetDids] = useState([]); // Store DIDs so we don't re-fetch fleet list
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [days, setDays] = useState(3);
  const limit = 20; // Items per page
  const dayChoices = [3, 7, 30];

  const userKey = readToken();
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  // 1. Fetch Fleet DIDs ONCE on mount
  useEffect(() => {
    const fetchFleet = async () => {
      try {
        const fleetConfig = {
          method: "get",
          url: `${apiBaseUrl}/api/fleetlist`,
          headers: { Authorization: `Bearer ${userKey}` },
        };
        const response = await axios.request(fleetConfig);
        const list = response.data.fleetlist;
        if (list && list.length > 0) {
          setFleetDids(list.map((device) => device.did));
        } else {
          setLoading(false); // No devices
        }
      } catch (err) {
        setError("Failed to fetch fleet list.");
        setLoading(false);
      }
    };
    fetchFleet();
  }, [apiBaseUrl, userKey]);

  // 2. Fetch Notifications whenever fleetDids or currentPage changes
  useEffect(() => {
    if (fleetDids.length === 0) return; // Wait until we have DIDs

    const fetchNotifications = async () => {
      try {
        setLoading(true);
        const notifConfig = {
          method: "post",
          url: `${apiBaseUrl}/api/notifications`,
          headers: { Authorization: `Bearer ${userKey}` },
          data: { 
            dids: fleetDids,
            page: currentPage,
            limit: limit,
            days
          },
        };
        
        const response = await axios.request(notifConfig);
        
        if (response.data.status === "success") {
          const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
          const rows = (response.data.data || []).filter((alert) => {
            const when = new Date(alert.gen_time).getTime();
            return !Number.isNaN(when) && when >= cutoff;
          });
          setAlerts(rows);
          setTotalPages(response.data.total_pages);
        } else {
          setError("Failed to load notifications.");
        }
      } catch (err) {
        setError("Error communicating with the server.");
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [fleetDids, currentPage, days, apiBaseUrl, userKey]);

  // Utility to style severity badges
  const getSeverityColor = (severity) => {
    switch (severity) {
      case "CRITICAL": return "bg-red-100 text-red-800";
      case "HIGH": return "bg-orange-100 text-orange-800";
      case "MEDIUM": return "bg-yellow-100 text-yellow-800";
      case "LOW": return "bg-green-100 text-green-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  // Pagination Handlers
  const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handleNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));

  return (
    <div className="mt-5 grid h-full grid-cols-1 gap-5">
      <Card extra={"w-full h-full p-4 sm:p-6 flex flex-col justify-between"}>
        <div>
          <header className="relative flex flex-wrap items-center justify-between gap-3 pt-4">
            <div className="text-xl font-bold text-navy-700 dark:text-white">
              Notifications & Alerts
            </div>
            <div className="flex items-center gap-2">
              {dayChoices.map((choice) => (
                <button
                  key={choice}
                  type="button"
                  onClick={() => {
                    setDays(choice);
                    setCurrentPage(1);
                  }}
                  className={`rounded-md px-3 py-2 text-sm font-semibold ${
                    days === choice
                      ? "bg-brand-500 text-white"
                      : "bg-lightPrimary text-navy-700 dark:bg-navy-900 dark:text-white"
                  }`}
                >
                  Last {choice} days
                </button>
              ))}
            </div>
          </header>

          <div className="mt-8 overflow-x-scroll xl:overflow-x-hidden">
            {loading && alerts.length === 0 ? (
              <div className="py-10 text-center font-medium text-gray-600">Loading alerts...</div>
            ) : error ? (
              <div className="py-10 text-center font-medium text-red-500">{error}</div>
            ) : alerts.length === 0 ? (
              <div className="py-10 text-center font-medium text-gray-600">No alerts in the last {days} days.</div>
            ) : (
              <table className="w-full">
                {/* Same Table Head and Body as before */}
                <thead>
                  <tr className="border-b-[1px] border-gray-200 text-start dark:border-white/10">
                    <th className="pb-3 text-start text-sm font-bold tracking-wide text-gray-600 lg:pb-4">Date & Time</th>
                    <th className="pb-3 text-start text-sm font-bold tracking-wide text-gray-600 lg:pb-4">Asset ID</th>
                    <th className="pb-3 text-start text-sm font-bold tracking-wide text-gray-600 lg:pb-4">Alert</th>
                    <th className="pb-3 text-start text-sm font-bold tracking-wide text-gray-600 lg:pb-4">Severity</th>
                    <th className="pb-3 text-start text-sm font-bold tracking-wide text-gray-600 lg:pb-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.map((alert) => (
                    <tr key={alert.log_id} className="border-b border-gray-100 hover:bg-gray-50 dark:border-white/10 dark:hover:bg-navy-800">
                      <td className="py-3 text-sm font-bold text-navy-700 dark:text-white">
                        {format(new Date(alert.gen_time), "dd MMM yyyy, HH:mm:ss")}
                      </td>
                      <td className="py-3 text-sm font-bold text-navy-700 dark:text-white">
                        {alert.aid || alert.did}
                      </td>
                      <td className="py-3 text-sm font-bold text-navy-700 dark:text-white">
                        {alert.alert_name}
                      </td>
                      <td className="py-3">
                        <span className={`px-2 py-1 rounded-md text-xs font-bold ${getSeverityColor(alert.severity)}`}>
                          {alert.severity}
                        </span>
                      </td>
                      <td className="py-3 text-sm font-medium text-gray-600 dark:text-white">
                        {alert.current_state}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4 dark:border-white/10">
            <p className="text-sm text-gray-600 dark:text-white">
              Showing Page <span className="font-bold">{currentPage}</span> of <span className="font-bold">{totalPages}</span>
            </p>
            <div className="flex space-x-2">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1 || loading}
                className={`rounded-md px-4 py-2 text-sm font-medium transition ${
                  currentPage === 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-navy-800"
                    : "bg-lightPrimary text-gray-700 hover:bg-gray-200 dark:bg-navy-900 dark:text-white dark:hover:bg-navy-800"
                }`}
              >
                Previous
              </button>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages || loading}
                className={`rounded-md px-4 py-2 text-sm font-medium transition ${
                  currentPage === totalPages
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-navy-800"
                    : "bg-lightPrimary text-gray-700 hover:bg-gray-200 dark:bg-navy-900 dark:text-white dark:hover:bg-navy-800"
                }`}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default Notifications;